














































import { defineComponent, ref, onBeforeMount, reactive, toRefs } from '@vue/composition-api';
import { ServiceFactory } from '@/service/ServiceFactory';
import { useStore } from '@/utils/use-store';

import { Exercise } from '@/helpers/types';
import QuestionsForm from '@/components/Questions/QuestionsForm.vue';
import QuestionsTable from '@/components/Questions/QuestionsTable.vue';
import { usePractice } from '@/composition-functions/usePractice';
import { useCategoryList } from '@/composition-functions/useCategory';

const ExerciseService = ServiceFactory.get('exercise');

export default defineComponent({
  name: 'QuestionsList',

  components: {
    QuestionsTable,
    QuestionsForm
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    }
  },

  setup(props, context) {
    const store = useStore();
    const { accessToken: token } = store.state.user;

    const { categoryList, fetchCategories } = useCategoryList();
    const {
      exerciseList,
      exerciseDrawer,
      mode,
      fetchExercises,
      toggleEdit,
      tempExercise,
      closeEventHandler,
      createExercise,
      updateExercise,
      requestPending,
      toggleCreate,
      archiveExercise,
    } = useExercises(token);

    const {
      practiceList,
      fetchPractice,
    } = usePractice(token);

    onBeforeMount(beforeMountEventHandler);

    /**
     * Handler for before mount event
     * */
    async function beforeMountEventHandler() {
      try {
        await fetchCategories(token);
        await fetchExercises(token);
        await fetchPractice();
      } catch (e) {
        throw e;
      }
    }

    return {
      categoryList,
      exerciseList,
      exerciseDrawer,
      mode,
      tempExercise,
      toggleEdit,
      closeEventHandler,
      createExercise,
      updateExercise,
      requestPending,
      practiceList,
      toggleCreate,
      archiveExercise,
    };
  }
});

export function useExercises(token: string): any {
  const exerciseState = reactive<{ [key: string]: any }>({
    exerciseList: [],
    exerciseDrawer: false,
    mode: 'create',
    tempExercise: null,
    requestPending: false,
  });

  /**
   * Fetch exercise list from api
   * @param {string} token
   * */
  async function fetchExercises(token: string) {
    try {
      const { data } = await ExerciseService.list(token);
      exerciseState.exerciseList = data;
    } catch (e) {
      const message: string = e.response ? e.response.data.message || e.response.data.error : e.message;
      throw Error(message);
    }
  }

  /**
   * Handler for toggle edit event
   * @param {string} id
   * */
  function toggleEdit(id: string) {
    exerciseState.tempExercise = Object.assign({}, exerciseState.exerciseList.find((exercise: Exercise) => exercise.id === id)) || null;
    exerciseState.mode = 'edit';
    exerciseState.exerciseDrawer = true;
  }

  /**
   * Handler for toggle create event
   * */
  function toggleCreate() {
    exerciseState.tempExercise = null;
    exerciseState.mode = 'create';
    exerciseState.exerciseDrawer = true;
  }

  /**
   * Handler for close exercise form event
   * */
  function closeEventHandler() {
    exerciseState.exerciseDrawer = false;
    setTimeout(() => {
      exerciseState.mode = 'create';
      exerciseState.tempExercise = null;
    }, 500);
  }

  /**
   * Handler for create exercise event
   * @param {Object} payload
   * @param {Exercise} payload.exercise
   * */
  async function createExercise({ exercise }: { exercise: Exercise }) {
    exerciseState.requestPending = true;
    try {
      await ExerciseService.create({ token, payload: exercise });
      await fetchExercises(token);
      closeEventHandler();
    } catch (e) {
      const message: string = e.response ? e.response.data.message || e.response.data.error : e.message;
      throw Error(message);
    } finally {
      exerciseState.requestPending = false;
    }
  }

  /**
   * Handler for update exercise
   * @param {object} payload
   * @param {object} payload.exercise
   * @param {number} payload.id
   * */
  async function updateExercise({ exercise, id }: { exercise: any, id: number }) {
    exerciseState.requestPending = true;
    try {
      const payload = Object.assign({}, exercise);
      Object.keys(payload).forEach(key => {
        if (typeof exercise[key] === 'object') {
          console.log(payload[key]);
          payload[key] = payload[key].id;
        }
      });
      await ExerciseService.edit({ id, token, payload: exercise });
      await fetchExercises(token);
      closeEventHandler();
    } catch (e) {
      const message: string = e.response ? e.response.data.message || e.response.data.error : e.message;
      throw Error(message);
    } finally {
      exerciseState.requestPending = false;
    }
  }

  /**
   * Handler for archive exercise event
   * @param {string} id
   * */
  async function archiveExercise(id: number) {
    try {
      await ExerciseService.edit({ id, token, payload: { active: false } });
      await fetchExercises(token);
      closeEventHandler();
    } catch (e) {
      const message: string = e.response ? e.response.data.message || e.response.data.error : e.message;
      throw Error(message);
    }
  }

  return {
    ...toRefs(exerciseState),
    fetchExercises,
    toggleEdit,
    closeEventHandler,
    createExercise,
    updateExercise,
    toggleCreate,
    archiveExercise,
  };
}
